import {
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@material-ui/core';
import { useEffect, useState } from 'react';
import ActivitiesTableItem from './ActivitiesTableItem';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { convertToFinalPrice } from '../../../finalPrice';
import { filterTicketsBySellingRange } from '../../../utils/ticketHelper';

function exportToExcel(activities) {
    const headers = ['Region', 'Vendor name', 'Activity name', 'Ticket name', 'Net price'];
    const rows = [];

    // iterate on all activities on all tickets and gather all the unique names in allPriceItems
    let allPriceItems = new Set();
    activities.forEach(activity => {
        activity.tickets.forEach(ticket => {
            if (!ticket.allPriceItems) {
                return;
            }
            ticket.allPriceItems.forEach(priceItem => {
                let n = priceItem.name;
                if (priceItem.is_fee) {
                    n = `[FEE] ${n}`; // add fee item
                }
                allPriceItems.add(n);
                if (priceItem.tax > 0) {
                    allPriceItems.add(`[TAX] ${n}`); // add tax item
                }
            });
        });
    });
    allPriceItems = Array.from(allPriceItems);
    // sort by name
    allPriceItems.sort();

    // create rows for each ticket
    activities.forEach(activity => {
        activity.tickets.forEach(ticket => {
            const row = [];
            row.push(activity?.island?.name || '');
            row.push(activity?.company?.name);
            row.push(activity.name);
            row.push(ticket.name);
            row.push(convertToFinalPrice(ticket.retail_price, ticket.final_price));

            allPriceItems.forEach(priceItemName => {
                // find the price item in the ticket
                let priceItem = ticket.allPriceItems.find(priceItem => {
                    let n = priceItem.name;
                    if (priceItem.is_fee) {
                        n = `[FEE] ${n}`; // add fee item
                    }
                    return n === priceItemName;
                });
                if (priceItem) {
                    row.push(priceItem.net_usd);
                    return;
                }
                // do the same if tax is present
                priceItem = ticket.allPriceItems.find(priceItem => {
                    let n = priceItem.name;
                    if (priceItem.is_fee) {
                        n = `[FEE] ${n}`; // add fee item
                    }
                    return `[TAX] ${n}` === priceItemName;
                });
                if (priceItem) {
                    row.push(priceItem.tax);
                    return;
                }
                // if not found, add empty cell
                row.push('');
            });
            rows.push(row);
        });
    });

    const excelData = [[...headers, ...allPriceItems], ...rows];
    // do the actual export
    const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Activities');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    const file = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(file, `tickets_${new Date().toISOString().substr(0, 10)}${fileExtension}`);
}

const ActivitiesTable = ({
    activities,
    currentFilters,
    setCurrentFilters,
    isAdmin,
    showCommission,
    showSellingRanges
}) => {
    const [mappedActivities, setMappedActivities] = useState([]);

    useEffect(() => {
        if (showSellingRanges) {
            const filteredActivities = activities.map(activity => {
                const filteredTickets = activity.tickets.filter(ticket => {
                    return (
                        filterTicketsBySellingRange(
                            [ticket],
                            currentFilters?.checkInDate,
                            currentFilters?.checkOutDate
                        ).length > 0
                    );
                });
                return {
                    ...activity,
                    tickets: filteredTickets
                };
            });
            setMappedActivities(filteredActivities);
        } else {
            setMappedActivities(activities);
        }
    }, [activities, currentFilters?.checkInDate, currentFilters?.checkOutDate, showSellingRanges]);

    if (activities?.length === 0) {
        return null;
    }

    return (
        <Grid container style={{ padding: 16 }}>
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {isAdmin && <TableCell>Region</TableCell>}
                            <TableCell>Vendor name</TableCell>
                            <TableCell>Activity name</TableCell>
                            <TableCell>Vendor phone number</TableCell>
                            <TableCell>First available date/time</TableCell>
                            <TableCell>Prices</TableCell>
                            <TableCell>
                                {isAdmin && (
                                    <Button
                                        size="small"
                                        color={'secondary'}
                                        onClick={() => {
                                            exportToExcel(mappedActivities);
                                        }}
                                    >
                                        Export
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {mappedActivities?.map(activity => (
                            <ActivitiesTableItem
                                key={activity.id}
                                activity={activity}
                                currentFilters={currentFilters}
                                setCurrentFilters={setCurrentFilters}
                                showCommission={showCommission}
                                showSellingRanges={showSellingRanges}
                                isAdmin={isAdmin}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
};

export default ActivitiesTable;
